/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import Feedback from 'components/lib/Feedback';
import React, {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import { getScore, setScore } from 'services/scoring';
import { unlockInteraction } from 'services/unlockInteraction';
import { State } from 'store/store';
import { interactionContext as _interactionContext } from './interactionContext';
import { ImageContext } from 'components/lib/Image/Image';
//
import CC_Multi_Choice_Media_Placeholders_1 from 'assets/CC_Multi Choice Media Placeholders_1.svg';
import CC_Multi_Choice_Media_Placeholders_2 from 'assets/CC_Multi Choice Media Placeholders_2.svg';
import CC_Multi_Choice_Media_Placeholders_3 from 'assets/CC_Multi Choice Media Placeholders_3.svg';
import CC_Multi_Choice_Media_Placeholders_4 from 'assets/CC_Multi Choice Media Placeholders_4.svg';
import CC_Multi_Choice_Media_Placeholders_5 from 'assets/CC_Multi Choice Media Placeholders_5.svg';
import CC_Multi_Choice_Media_Placeholders_6 from 'assets/CC_Multi Choice Media Placeholders_6.svg';
import pubSub from 'services/pubSub';
import { Report } from 'store/store';

const multichoiceMediaPlaceholders = [
  CC_Multi_Choice_Media_Placeholders_1,
  CC_Multi_Choice_Media_Placeholders_2,
  CC_Multi_Choice_Media_Placeholders_3,
  CC_Multi_Choice_Media_Placeholders_4,
  CC_Multi_Choice_Media_Placeholders_5,
  CC_Multi_Choice_Media_Placeholders_6,
];

// this will create the relationship of id to placeholder image
// for the current session
const placeholderMappingToImageId = {};

enum SubmitState {
  Initial = 0,
  Resolved = 2,
}

interface MultichoiceProps {
  children: ReactElement[] | ReactElement | string | null;
}

interface MultichoiceContext {
  selected: number | string[];
  updateSelected: (optionId: number | string) => void;
  correct: number | string[];
  isMultiSelect: boolean;
  answerCount: number;
  isLive: boolean;
  randomizeOptions?: boolean;
  submit: SubmitState;
  onSubmit: () => void;
}

const MultichoiceContext = createContext<MultichoiceContext | undefined>(
  undefined
);

// keeping this local to the session
// if the interaction is set to score &&
// if the score is not undefined ( has value )
// true
let sessionLock = {};
let attempts = {};

let randomAnswers = {};

pubSub.subscribe('RESET_MODULE', () => {
  console.log('multichoice resets');

  sessionLock = {};
  attempts = {};

  randomAnswers = {};
});

const getRandomAnswers = (id, Answers) => {
  if (!randomAnswers[id]) {
    randomAnswers[id] = [...Answers].sort(() => Math.random() - 0.5);
  }

  return randomAnswers[id];
};

const Multichoice = (props: MultichoiceProps) => {
  const interactionContext = useContext(_interactionContext);

  const { id: interactionContextId } = interactionContext;
  let {
    correctOptions = '',
    score,
    randomizeOptions = false,
    useFeedback = true,
    useMediaInFeedback = true,
    useUniqueOptionFeedback,
    showAttemptNumber,
    showCorrectAnswer,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },

    isMultichoiceMedia,
    mediaPanelWidth = 320,

    showReviewFeedback = false,
  } = interactionContext.props;

  let { includeQuestionTextPlusImage } = interactionContext.props; // undefined by default. falsey

  const numberOfAttempts = interactionContext.hasAttemptFeedback
    ? interactionContext.props.numberOfAttempts
    : 0;

  // state (s)
  //
  const [selected, setSelected] = useState<any[]>([]);
  const [submit, setSubmit] = useState(
    sessionLock[interactionContextId]
      ? SubmitState.Resolved
      : SubmitState.Initial
  );
  const [feedback, setFeedback] = useState(false);

  const scoringQuizzes = useScoringQuizzes();
  const { scoreAll } = useSelector((state: State) => {
    return Object.values(state.blocks).find(b => b.type === 'Module')
      ?.props as any;
  });

  sessionLock[interactionContextId] =
    attempts[interactionContextId] === numberOfAttempts &&
    score &&
    getScore(interactionContextId) !== undefined;

  const dispatch = useDispatch();

  const { children } = props as any;
  const [Question, SubmitBtn, AnswersContainer, ReviewFeedback] = children;

  const Answers = AnswersContainer.props.children;

  // refs
  const multichoice = useRef<HTMLFormElement>(null);

  if (!attempts[interactionContextId]) {
    attempts[interactionContextId] = 0;
  }
  const isCorrect = useRef(false);

  // correctOptions array from string
  correctOptions = correctOptions
    .split(/,/g)
    .filter(entry => entry !== '')
    .sort();

  // Correct contains an array of ids of correct answers
  const correct = correctOptions.map((o, i) => {
    return Answers[o - 1]?.props?.id;
  });
  const isMultiCorrect = correct.length > 1;
  const isLive = selected.length && submit === 0;

  // Toggle options
  const updateSelected = optionId => {
    let _selected;

    if (isMultiCorrect) {
      if (selected.includes(optionId)) {
        // Toggle off
        _selected = selected.filter(o => optionId !== o);
      } else {
        // Toggle on
        _selected = [...selected, optionId];
      }
    } else {
      _selected = selected[0] === optionId ? [] : [optionId];
    }

    // Maintain "correct" state
    isCorrect.current = _selected.sort().join() === correct.sort().join();

    // updating setScore for old ways ( display results )
    setScore(interactionContextId, isCorrect.current);
    //
    //

    setSelected(_selected);
  };

  const showFeedback = correct => {
    useFeedback && setFeedback(true);

    if (window.dev()) {
      return;
    }

    if (
      // score &&
      correct ||
      attempts[interactionContextId] >= numberOfAttempts
    ) {
      //
      let questionText: HTMLDivElement | string = document.querySelector(
        !isMultichoiceMedia
          ? `#i${interactionContext.id} form .chameleon-text`
          : `#multichoice-media-question-text-${interactionContext.id}`
      ) as HTMLDivElement;

      if (questionText) {
        questionText = questionText?.innerText;
      }

      const response = selected
        .map(s => {
          let result = '-1';
          Answers.forEach((a, i) => {
            if (s === a.props.id) {
              result = `${i + 1}`;
              return;
            }
          });
          return result;
        })
        .sort()
        .join(',');

      const getCorrectAndResponseOptions = entry => {
        let answer = '';
        Answers.forEach((a, i) => {
          if (entry === a.props.id) {
            const { id } = a.props;
            const content = document.querySelector(
              `#i${id}`
            ) as HTMLInputElement;

            answer = content?.innerText;

            if (!answer && isMultichoiceMedia) {
              // get the alt off the image
              const imgAltText = content.querySelector('img')?.alt || '';
              answer = imgAltText;
            }
          }
        });

        return answer;
      };

      const responseExpanded = selected
        .map(getCorrectAndResponseOptions)
        .join(',');

      const correctExpanded = correctOptions
        .map((o, i) => {
          return Answers[o - 1]?.props?.id;
        })
        .map(getCorrectAndResponseOptions)
        .join(',');

      const action: Report = {
        id: interactionContextId,
        type: 'fill-in',
        // 'choice',
        question: questionText,
        reportable: 'multichoice' as 'multichoice',
        // ,
        response: responseExpanded,
        correct: correctExpanded,
        result: correct ? 1 : 0,
      };

      // console.log(JSON.stringify(action, null, 2));

      // dispatch if there's no scoringQuizzes etc
      let shouldDispatchScore = score;

      if (scoringQuizzes.length) {
        shouldDispatchScore = false;
      }

      if (shouldDispatchScore) {
        dispatch(updateScoreAction(interactionContextId, correct ? 1 : 0));
      }

      dispatch(addScormResponseAction(action));
    }

    //
    // we do our unlocking here if !useFeedback
    //
    const unlock =
      isCorrect.current || attempts[interactionContextId] === numberOfAttempts;

    if (!useFeedback && unlock) {
      setSubmit(SubmitState.Resolved);
      unlockInteraction(interactionContextId);
    }
  };

  const onSubmit = () => {
    attempts[interactionContextId] += 1;
    showFeedback(isCorrect.current);
  };

  const onClose = () => {
    setFeedback(false);
  };

  // effects
  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    try {
      if (
        parent.multichoiceIsLive &&
        parent.multichoiceIsLive[interactionContextId]
      ) {
        parent.multichoiceIsLive[interactionContextId]({
          isLive: !!isLive,
          isCorrect: isCorrect.current,
        });
      }

      if (parent.showFeedback) {
        parent.showFeedback[interactionContextId] = correct => {
          if (useUniqueOptionFeedback && !isMultiCorrect && !isLive) {
            return alert('No options have been selected');
          }

          if (correct === null) {
            return setSelected([]);
          }

          if (correct === 'attempt') {
            //
          }

          if (feedback) {
            setFeedback(false);
            requestAnimationFrame(() =>
              showFeedback((isCorrect.current = correct))
            );
            //
            return;
          }

          showFeedback((isCorrect.current = correct));
        };
      }
    } catch (err) {
      console.log(err);
    }
  });

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const content = Array.from(
      multichoice.current!.querySelectorAll('label')
    ) as any;

    const answerIds = Answers.map((answer: any) => answer.props.id);

    const answers = content.map((t: any) => t.innerText);

    const alphabetized = selected.map((entry: any) => {
      return 'abcdefghijklmnopqrstuvwxyz'.split('')[answerIds.indexOf(entry)];
    });
  });

  let parsedIsCorrect;

  if (
    !useUniqueOptionFeedback &&
    attempts[interactionContextId] < numberOfAttempts
  ) {
    parsedIsCorrect = isCorrect.current || 'attempt';
  } else {
    parsedIsCorrect = isCorrect.current;
  }

  const _Answers_ =
    window.dev && !window.dev() && randomizeOptions
      ? getRandomAnswers(interactionContextId, Answers)
      : Answers;

  const feedbackOpenerButton = useRef<HTMLButtonElement>();

  return (
    <MultichoiceContext.Provider
      value={{
        isMultiSelect: isMultiCorrect,
        selected,
        isLive: !!isLive,
        correct,
        updateSelected,
        submit,
        answerCount: Answers.length || 1,
        randomizeOptions,
        onSubmit: () => {
          attempts[interactionContextId] += 1;
          showFeedback(isCorrect.current);
        },
      }}
    >
      <form ref={multichoice} onSubmit={e => e.preventDefault()}>
        {!isMultichoiceMedia || includeQuestionTextPlusImage ? (
          <div style={{ marginBottom: 15 }}>{Question}</div>
        ) : null}

        {isMultichoiceMedia ? (
          <div
            key={`multichoice-media-container-${_Answers_.length}`}
            css={css({
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: ['space-between', 'center'][1],

              width: '100%',

              '> div': {
                margin: '10px',
                // auto
              },

              // '[class*="EditWrapper"]': {
              //   left: 10,
              //   top: 10,
              //   width: 'calc(100% - 20px)',
              //   height: 'calc(100% - 20px)',
              // },

              '.chameleon-image': {
                flexShrink: 0,
              },

              '.chameleon-text': {
                margin: '10px 0 0 0',
              },
            })}
          >
            {_Answers_.map((answer, index) => {
              const { id } = answer?.props;

              const placeholder =
                multichoiceMediaPlaceholders[
                  index % multichoiceMediaPlaceholders.length
                ];

              if (!placeholderMappingToImageId[id]) {
                placeholderMappingToImageId[id] = placeholder;
              }

              return (
                <ImageContext.Provider
                  key={`image-context-provider-${id}`}
                  value={{
                    //
                    editWrapperType: 1,
                    editWrapperSize: '80%',
                    placeholder: placeholderMappingToImageId[id],
                    canUpdateContainerHeight: false,
                  }}
                >
                  {answer}
                </ImageContext.Provider>
              );
            })}
          </div>
        ) : (
          _Answers_
        )}

        {(!showReviewFeedback || submit !== SubmitState.Resolved) && (
          <div
            css={css({
              margin: '20px 0 0 0',
              ...(!window.dev() && {
                opacity: isLive ? 1 : 0,
                pointerEvents: isLive ? 'auto' : 'none',
                transition: 'opacity 0.2s',
              }),

              ...(isMultichoiceMedia && {
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }),
            })}
          >
            {React.cloneElement(SubmitBtn, {
              enabled: !!isLive,
              onClick: onSubmit,
            })}
          </div>
        )}

        {showReviewFeedback &&
          (window.dev() || submit === SubmitState.Resolved) && (
            <div
              style={{
                margin: window.dev() ? '25px 0 0 0' : '20px 0 0 0',
                ...(isMultichoiceMedia && {
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }),
              }}
            >
              {React.cloneElement(ReviewFeedback, {
                onClick() {
                  showFeedback(parsedIsCorrect);
                },
              })}
            </div>
          )}
      </form>

      {feedback && (
        <Feedback
          id={
            !isMultiCorrect
              ? !useUniqueOptionFeedback
                ? interactionContextId
                : selected[0]
              : interactionContextId
          }
          correct={window.dev() ? isCorrect.current : parsedIsCorrect}
          useAttemptFeedback={!useUniqueOptionFeedback}
          attempt={attempts[interactionContextId]}
          numberOfAttempts={numberOfAttempts}
          showAttemptNumber={showAttemptNumber}
          useMediaInFeedback={useMediaInFeedback}
          navigationBehaviour={navigationBehaviour}
          onOpened={(...$) => {
            if (window.dev()) {
              return;
            }
          }}
          onClose={() => {
            if (!window.dev()) {
              const unlock =
                isCorrect.current ||
                attempts[interactionContextId] === numberOfAttempts;

              if (unlock) {
                setSubmit(SubmitState.Resolved);
                unlockInteraction(interactionContextId);
              }
            }

            const { correct, incorrect } = navigationBehaviour;
            // if any of these checks is true. we want to return focus back to the button we're in
            // or. perhaps we give focus back to the question > options ?
            if (parsedIsCorrect === 'attempt' || correct || incorrect) {
              const section = document.querySelector(
                `#i${interactionContextId}`
              ) as HTMLElement;
              //
              const feedbackButton =
                section &&
                (section.querySelector(
                  '[class*="genericButton"]'
                ) as HTMLButtonElement);
              //
              const focusedElement = feedbackButton || section;

              let abortCounter = 0;

              const tick = setInterval(() => {
                if (document.activeElement === focusedElement) {
                  clearInterval(tick);
                  return;
                }

                if (abortCounter >= 10) {
                  console.warn(
                    'something has somethinged and we cannot give button focus'
                  );
                  clearInterval(tick);
                  return;
                }

                //

                focusedElement?.focus();
                abortCounter += 1;
                //
              }, 10);

              //
            }

            onClose();
          }}
          {...interactionContext.props}
        />
      )}
    </MultichoiceContext.Provider>
  );
};

export default Multichoice;

export { MultichoiceContext };
